/**
 * Toast context + provider + `useToast` hook. Wrap the app in `<ToastProvider>`
 * (done in main.tsx) and call `useToast()` anywhere to push notifications:
 *   const toast = useToast();
 *   toast.success('Đã lưu!');
 * Toasts auto-dismiss after `DEFAULT_DURATION` ms and render via a portal.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import Toast, { type ToastType } from '@/components/Alerts/Toast';

/** A queued toast. */
export interface ToastItem {
  id: string;
  type: ToastType;
  message: string;
}

/** Value exposed by {@link useToast}. */
export interface ToastContextValue {
  toasts: ToastItem[];
  show: (type: ToastType, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  warning: (message: string) => void;
  info: (message: string) => void;
  dismiss: (id: string) => void;
}

const DEFAULT_DURATION = 5000;

const ToastContext = createContext<ToastContextValue | null>(null);

/** Generate a reasonably unique id for a toast. */
function makeId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Provides toast state to the tree and renders the toast stack via a portal. */
export function ToastProvider({ children }: { children: ReactNode }): ReactElement {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  // Track timers so we can clear them on manual dismiss.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const show = useCallback(
    (type: ToastType, message: string) => {
      const id = makeId();
      setToasts((prev) => [...prev, { id, type, message }]);
      const timer = setTimeout(() => dismiss(id), DEFAULT_DURATION);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      show,
      success: (message: string) => show('success', message),
      error: (message: string) => show('error', message),
      warning: (message: string) => show('warning', message),
      info: (message: string) => show('info', message),
      dismiss,
    }),
    [toasts, show, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-2"
            role="region"
            aria-label="Thông báo"
          >
            {toasts.map((t) => (
              <Toast key={t.id} type={t.type} message={t.message} onClose={() => dismiss(t.id)} />
            ))}
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

/** Access the toast API. Must be used within a {@link ToastProvider}. */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
